import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { doc, getDoc, setDoc, updateDoc } from "firebase/firestore";
import {
  handleSignIn,
  setChatSessions,
  setDarkMode,
  setTrialMessages
} from "./chatSlice";

import { db } from "../utils/firebase";
import { getIpAddress } from "../utils/Functions";

const fireSlice = createSlice({
  name: "fire",
  initialState: {
    retrieved: false,
    ipAddress: null
  },
  reducers: {
    setRetrieved(state, action) {
      state.retrieved = action.payload;
    },
    setIpAddress(state, action) {
      state.ipAddress = action.payload;
    }
  }
});

export const getOrCreateUser = createAsyncThunk(
  "fire/checkAndCreateUser",
  async (user, { dispatch, getState }) => {
    try {
      const userDocRef = doc(db, "users", user.uid);
      const userDocSnapshot = await getDoc(userDocRef);

      if (!userDocSnapshot.exists()) {
        console.log("User does not exist, creating new user...");

        const userData = {
          createdAt: new Date(),
          darkMode: false,
          chatSessions: []
        };

        await setDoc(userDocRef, userData);

        console.log("New user created successfully in Firestore");
      } else if (!getState().fire.retrieved) {
        console.log("User already exists in Firestore");

        const userData = userDocSnapshot.data();
        if (userData.chatSessions && userData.chatSessions.length > 0) {
          console.log(
            "User has existing chat sessions:",
            userData.chatSessions
          );
          dispatch(setRetrieved(true));

          let chatSessions = getState().chat.chatSessions.concat(
            userData.chatSessions
              .filter((session) => session.messages.length > 0)
              .sort((a, b) => b.lastActive - a.lastActive)
          );

          dispatch(setChatSessions(chatSessions));

          if (userData.hasOwnProperty("darkMode")) {
            dispatch(setDarkMode(userData.darkMode));
          }
        }
      }
    } catch (error) {
      console.error("Error checking or creating user in Firestore:", error);
    }
  }
);

export const updateChatSessions = createAsyncThunk(
  "fire/updateChatSessions",
  async ({ userId, chatSessions }, { rejectWithValue }) => {
    console.log("User ID: ", userId);
    try {
      const userDocRef = doc(db, "users", userId);

      await updateDoc(userDocRef, {
        chatSessions: chatSessions
      });

      console.log("Chat sessions updated in Firestore");
    } catch (error) {
      console.error("Error updating chat sessions in Firestore:", error);
      return rejectWithValue(error.message);
    }
  }
);

export const getOrCreateTrialMessages = createAsyncThunk(
  "fire/getOrCreateTrialMessages",
  async (_, { dispatch }) => {
    try {
      const ipAddress = await getIpAddress();
      if (!ipAddress) {
        console.error("Unable to retrieve IP address.");
        return;
      }

      dispatch(setIpAddress(ipAddress));

      const trialDocRef = doc(db, "trialMessages", ipAddress);
      const trialDocSnapshot = await getDoc(trialDocRef);

      if (!trialDocSnapshot.exists()) {
        console.log("Trial document does not exist, creating new one...");

        const trialData = {
          timestamps: []
        };

        await setDoc(trialDocRef, trialData);

        console.log("New trial document created successfully in Firestore");
      } else {
        console.log("Trial document already exists in Firestore");

        const trialData = trialDocSnapshot.data();
        if (trialData.timestamps && trialData.timestamps.length > 0) {
          // Filter timestamps over 24 hours
          const currentTime = Date.now();
          const filteredTimestamps = trialData.timestamps.filter(
            (timestamp) => currentTime - timestamp < 24 * 60 * 60 * 1000
          );
          console.log("filtered timestamps:", filteredTimestamps);

          dispatch(setTrialMessages(filteredTimestamps));
        }
      }
    } catch (error) {
      console.error(
        "Error checking or creating trial messages in Firestore:",
        error
      );
    }
  }
);

export const addTrialMessage = createAsyncThunk(
  "fire/addTrialMessage",
  async (trialMessages, { dispatch, getState, rejectWithValue }) => {
    try {
      const state = getState();
      const userIp = state.fire.ipAddress;

      if (!userIp) {
        console.error("IP address is not available in the Redux state.");
        return rejectWithValue("IP address is not available.");
      }

      const trialDocRef = doc(db, "trialMessages", userIp);

      const updatedTimestamps = [...trialMessages, Date.now()];

      await updateDoc(trialDocRef, {
        timestamps: updatedTimestamps
      });
      dispatch(setTrialMessages(updatedTimestamps));

      console.log("Trial messages updated in Firestore");

      if (updatedTimestamps.length >= 5) {
        dispatch(handleSignIn());
      }
    } catch (error) {
      console.error("Error updating trial messages in Firestore:", error);
      return rejectWithValue(error.message);
    }
  }
);

export const setColorMode = createAsyncThunk(
  "fire/setColorMode",
  async ({ userId, darkMode }, { rejectWithValue }) => {
    try {
      const userDocRef = doc(db, "users", userId);

      await updateDoc(userDocRef, {
        darkMode: darkMode
      });
    } catch (error) {
      console.error("Error updating color mode in Firestore:", error);
      return rejectWithValue(error.message);
    }
  }
);

export const { setRetrieved, setIpAddress } = fireSlice.actions;

export default fireSlice.reducer;
